// src/components/InactivityLogout.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { signOut, onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { auth } from "../firebase";

const INACTIVITY_LIMIT_MS = 30 * 60 * 1000; // 30 minutes
const WARNING_BEFORE_LOGOUT_MS = 2 * 60 * 1000; // show warning 2 minutes before logout
const WARNING_AT_MS = INACTIVITY_LIMIT_MS - WARNING_BEFORE_LOGOUT_MS;

export default function InactivityLogout() {
  const navigate = useNavigate();

  const [showWarning, setShowWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(
    WARNING_BEFORE_LOGOUT_MS / 1000
  );

  const warningTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const warningOpenRef = useRef(false);
  const loggingOutRef = useRef(false);

  const clearTimers = useCallback(() => {
    if (warningTimerRef.current) {
      window.clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }

    if (logoutTimerRef.current) {
      window.clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }

    if (countdownTimerRef.current) {
      window.clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  }, []);

  const logoutUser = useCallback(async () => {
    if (loggingOutRef.current) return;

    loggingOutRef.current = true;
    clearTimers();

    try {
      await signOut(auth);
    } catch (error) {
      console.error("Automatic logout failed:", error);
    } finally {
      warningOpenRef.current = false;
      setShowWarning(false);

      navigate("/login", {
        replace: true,
        state: {
          sessionExpired: true,
        },
      });
    }
  }, [clearTimers, navigate]);

  const openWarning = useCallback(() => {
    warningOpenRef.current = true;
    setShowWarning(true);
    setSecondsRemaining(WARNING_BEFORE_LOGOUT_MS / 1000);

    countdownTimerRef.current = window.setInterval(() => {
      setSecondsRemaining((previousSeconds) => {
        if (previousSeconds <= 1) {
          window.clearInterval(countdownTimerRef.current);
          countdownTimerRef.current = null;
          return 0;
        }

        return previousSeconds - 1;
      });
    }, 1000);
  }, []);

  const startInactivityTimers = useCallback(() => {
    clearTimers();

    warningOpenRef.current = false;
    loggingOutRef.current = false;

    setShowWarning(false);
    setSecondsRemaining(WARNING_BEFORE_LOGOUT_MS / 1000);

    warningTimerRef.current = window.setTimeout(() => {
      openWarning();
    }, WARNING_AT_MS);

    logoutTimerRef.current = window.setTimeout(() => {
      logoutUser();
    }, INACTIVITY_LIMIT_MS);
  }, [clearTimers, logoutUser, openWarning]);

  const registerActivity = useCallback(() => {
    if (!auth.currentUser) return;

    // Once the popup is visible, the user must actively click
    // "Continue session" rather than resetting the timer accidentally.
    if (warningOpenRef.current) return;

    startInactivityTimers();
  }, [startInactivityTimers]);

  const continueSession = () => {
    startInactivityTimers();
  };

  useEffect(() => {
    const activityEvents = [
      "pointerdown",
      "keydown",
      "scroll",
      "touchstart",
    ];

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      clearTimers();

      if (user) {
        startInactivityTimers();
      } else {
        warningOpenRef.current = false;
        setShowWarning(false);
      }
    });

    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, registerActivity, {
        passive: true,
      });
    });

    return () => {
      unsubscribe();
      clearTimers();

      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, registerActivity);
      });
    };
  }, [clearTimers, registerActivity, startInactivityTimers]);

  if (!showWarning) {
    return null;
  }

  const minutes = Math.floor(secondsRemaining / 60);
  const seconds = String(secondsRemaining % 60).padStart(2, "0");

  return (
    <div style={overlayStyle} role="presentation">
      <div
        style={modalStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby="session-warning-title"
        aria-describedby="session-warning-description"
      >
        <h2 id="session-warning-title" style={titleStyle}>
          Your session is about to expire
        </h2>

        <p id="session-warning-description" style={textStyle}>
          You have been inactive for a while. For security reasons, you will be
          logged out in{" "}
          <strong>
            {minutes}:{seconds}
          </strong>
          .
        </p>

        <p style={smallTextStyle}>
          Continue your session to remain logged in.
        </p>

        <div style={buttonRowStyle}>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={logoutUser}
            style={secondaryButtonStyle}
          >
            Log out now
          </button>

          <button
            type="button"
            className="btn btn--primary"
            onClick={continueSession}
            style={primaryButtonStyle}
            autoFocus
          >
            Continue session
          </button>
        </div>
      </div>
    </div>
  );
}

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(15, 23, 42, 0.48)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: 20,
  zIndex: 9999,
};

const modalStyle = {
  width: "100%",
  maxWidth: 440,
  background: "#ffffff",
  borderRadius: 18,
  padding: "28px 26px",
  boxShadow: "0 20px 55px rgba(15, 23, 42, 0.18)",
};

const titleStyle = {
  margin: "0 0 12px",
  color: "#111827",
  fontSize: 22,
  lineHeight: 1.25,
};

const textStyle = {
  margin: "0 0 10px",
  color: "#374151",
  fontSize: 15,
  lineHeight: 1.6,
};

const smallTextStyle = {
  margin: "0 0 22px",
  color: "#64748b",
  fontSize: 14,
  lineHeight: 1.5,
};

const buttonRowStyle = {
  display: "flex",
  gap: 10,
  justifyContent: "flex-end",
  flexWrap: "wrap",
};

const secondaryButtonStyle = {
  minWidth: 120,
};

const primaryButtonStyle = {
  minWidth: 150,
};